import _ from 'underscore';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {StorableSummary} from 'constants/SampleConstants';
import {Tag} from 'folder-items/FileConstants';
import {
  ColumnDefinition,
  ConcentrationColumnDefinition,
  getEvaluatedCellOfColumnType,
  getOptionalEvaluatedCellOfColumnType,
  QuantityColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, TableColumnType} from 'notebook/tables/TableConstants';
import {
  coerceToPrimitive,
  convertTypedValueToTag,
  formatTypedValue,
  isNumeric,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {InvalidPositionError, parseGridPosition, Position} from 'samples/SampleUtil';
import {GridSchema} from 'samples/storables/StorableSchemaConstants';
import StorableStore from 'samples/StorableStore';
import {getTagUpdateParams} from 'schemas/tags/TagUtil';
import ErrorSubclass from 'util/ErrorSubclass';

export class StringResolutionError extends ErrorSubclass {}

export function getSourceBatchIdForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): string | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.SAMPLE,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    evaluatedRow: evaluatedEditableRow,
  });
  if (typedValue.type === BaseTypedValueType.BATCH) {
    return typedValue.batchInfo.id;
  } else {
    assert(typedValue.type === BaseTypedValueType.BIOENTITY, 'Unexpected typedValue in sample cell', {
      typedValue,
    });
    return null;
  }
}

export function getSourceBioentityIdForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): string | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.SAMPLE,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    evaluatedRow: evaluatedEditableRow,
  });
  if (typedValue.type === BaseTypedValueType.BIOENTITY) {
    return typedValue.bioentitySummary.id;
  } else {
    assert(typedValue.type === BaseTypedValueType.BATCH, 'Unexpected typedValue in sample cell', {
      typedValue,
    });
    return null;
  }
}

export function getSourceContainerIdForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): string | null {
  const cell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    evaluatedRow: evaluatedEditableRow,
  });
  if (cell == null) {
    return null;
  }
  if (isTypedValueEmpty(cell.value)) {
    return null;
  }
  if (cell.value.type !== BaseTypedValueType.STORABLES) {
    assert(false, 'Unexpected typedValue in source container cell', {typedValue: cell.value});
    return null;
  }
  assert(cell.value.storableSummaries.length === 1, 'Unexpected extra containers in source container cell', {
    typedValue: cell.value,
  });
  return cell.value.storableSummaries[0].id;
}

export function getSourcePlateIdForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): string | null {
  const cell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.PLATE,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    evaluatedRow: evaluatedEditableRow,
  });
  if (cell == null) {
    return null;
  }
  if (isTypedValueEmpty(cell.value)) {
    return null;
  }
  if (cell.value.type !== BaseTypedValueType.STORABLES) {
    assert(false, 'Unexpected typedValue in source plate cell', {typedValue: cell.value});
    return null;
  }
  assert(cell.value.storableSummaries.length === 1, 'Unexpected extra storables in source plate cell', {
    typedValue: cell.value,
  });
  return cell.value.storableSummaries[0].id;
}

export function getSourcePositionForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): {rowIndex: number; columnIndex: number} | null {
  const cell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.POSITION,
    tableColumnDefinitions: columnDefinitions,
    columnGroup: TransferTableColumnGroup.SOURCE,
    evaluatedRow: evaluatedEditableRow,
  });
  if (cell == null) {
    return null;
  }
  if (isTypedValueEmpty(cell.value)) {
    return null;
  }
  if (cell.value.type !== BaseTypedValueType.GRID_COORDINATES) {
    assert(false, 'Unexpected typedValue in source position cell', {typedValue: cell.value});
    return null;
  }
  return cell.value.gridCoordinates;
}

export function getStageRunIdForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): string | null {
  const cell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.STAGE_RUN,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });
  if (cell == null) {
    return null;
  }
  if (cell.value.type !== BaseTypedValueType.STAGE_RUN) {
    assert(false, 'Unexpected typedValue in stage run cell', {typedValue: cell.value});
    return null;
  }
  return cell.value.stageRunId;
}

export function getParentStorableForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): StorableInfo;

export function getParentStorableForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  params: {isRequired: true}
): StorableInfo;

export function getParentStorableForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  params: {isRequired: false}
): StorableInfo | null;

export function getParentStorableForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  {isRequired}: {isRequired: boolean} = {isRequired: true}
): StorableInfo | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.PARENT_STORABLE,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });
  if (typedValue.type !== BaseTypedValueType.STORABLES) {
    if (isRequired) {
      assert(false, 'Unexpected typedValue in parent storable cell', {typedValue});
    }
    return null;
  }
  return typedValue.storableSummaries[0];
}
/**
 * Version of getParentStorableForRow for new Transfer Structured Table
 *  - Destination Column is either TableColumnType.PLATE or TableColumnType.BOX
 */
export function getDestinationPlateOrBoxForRow(
  evaluatedRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  {isRequired}: {isRequired: boolean} = {isRequired: true},
  columnDefinitionType: TableColumnType
): StorableInfo | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnGroup: TransferTableColumnGroup.TARGET,
    columnDefinitionType,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow,
  });
  if (typedValue.type !== BaseTypedValueType.STORABLES) {
    if (isRequired) {
      assert(false, 'Unexpected typedValue in parent storable cell', {typedValue});
    }
    return null;
  }
  return typedValue.storableSummaries[0];
}

export function getPositionStringsForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): Array<string> {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnGroup: TransferTableColumnGroup.TARGET,
    columnDefinitionType: TableColumnType.POSITION,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });
  const textValue = formatTypedValue(typedValue);
  if (textValue.trim() === '') {
    return [];
  }
  return splitCommaSeparatedString(textValue);
}

export function getPositionsForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  destinationColumnDefinitionType?: TableColumnType
): Array<Position> {
  let parentStorableSummary;
  if (
    destinationColumnDefinitionType == null ||
    destinationColumnDefinitionType === TableColumnType.PARENT_STORABLE
  ) {
    // Old transfer table uses parent storable column type
    parentStorableSummary = getParentStorableForRow(evaluatedEditableRow, columnDefinitions, {
      isRequired: false,
    });
  } else {
    parentStorableSummary = getDestinationPlateOrBoxForRow(
      evaluatedEditableRow,
      columnDefinitions,
      {isRequired: false},
      destinationColumnDefinitionType
    );
  }
  const positionStrings = getPositionStringsForRow(evaluatedEditableRow, columnDefinitions);
  if (parentStorableSummary == null) {
    return [];
  }
  const grid = StorableStore.getGridSummary(parentStorableSummary.id);
  if (!grid) {
    return [];
  }
  return parsePositionStrings(positionStrings, grid);
}

export function splitCommaSeparatedString(text: string): Array<string> {
  return text.split(',').map((item) => item.trim());
}

/**
 * Validate and parse one or more strings representing grid positions against a target grid.
 *
 * @param positionStrings one or more strings, e.g. ['A2', 'C5']
 * @param parentStorableSummary the target grid summary object
 * @returns an array of parsed positions if all are valid.
 */
export function parsePositionStrings(
  positionStrings: Array<string>,
  parentStorableSummary: StorableSummary
): Array<Position> {
  const parentStorableSchema = parentStorableSummary.storableSchema as GridSchema;
  // oxlint-disable-next-line array-callback-return
  const positions = positionStrings.map((position) => {
    try {
      return parseGridPosition({
        position,
        gridHeight: parentStorableSchema.height,
        gridWidth: parentStorableSchema.width,
      });
    } catch (e) {
      if (e instanceof InvalidPositionError) {
        throw new StringResolutionError(
          `${position} is not a valid position in ${parentStorableSummary.name}.`
        );
      } else {
        throw e;
      }
    }
  });
  return positions;
}

export function getContainerBarcodesForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): Array<string> | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnGroup: TransferTableColumnGroup.TARGET,
    columnDefinitionType: TableColumnType.CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });
  if (typedValue.type === BaseTypedValueType.STRING) {
    const textValue = typedValue.rawValue;
    if (textValue.trim() === '') {
      return null;
    }
    return splitCommaSeparatedString(textValue);
  }
  assert(
    typedValue.type === BaseTypedValueType.STORABLE_PLACEHOLDERS,
    'Unexpected typedValue in containers cell',
    {
      typedValue,
    }
  );
  return null;
}

export function getNumberOfContainersForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): number;

export function getNumberOfContainersForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  params: {isRequired: true}
): number;

export function getNumberOfContainersForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  params: {isRequired: false}
): number | null;

export function getNumberOfContainersForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>,
  {isRequired}: {isRequired: boolean} = {isRequired: true}
): number | null {
  const {value: typedValue} = getEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.NUMBER_OF_CONTAINERS,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });
  if (!isNumeric(typedValue)) {
    if (isRequired) {
      assert(false, 'Unexpected typedValue in number of containers cell', {typedValue});
    }
    return null;
  }
  const primitive = coerceToPrimitive(typedValue);
  if (typeof primitive !== 'number') {
    return null;
  }
  return primitive;
}

export function getVolumeForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): LegacyMeasurement | null {
  const evaluatedCell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.QUANTITY,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });

  if (evaluatedCell == null) {
    return null;
  }
  const {value: typedValue} = evaluatedCell;
  if (isTypedValueEmpty(typedValue)) {
    return null;
  }

  assert(isNumeric(typedValue), 'Unexpected typedValue in volume cell', {
    typedValue,
  });

  const primitive = coerceToPrimitive(typedValue);
  const volumeValue = typeof primitive === 'number' ? primitive : null;
  const volumeColumnDefinition = _.findWhere(columnDefinitions, {
    type: TableColumnType.QUANTITY,
  }) as QuantityColumnDefinition;
  return {
    value: volumeValue,
    units: volumeColumnDefinition.units,
  };
}

export function getConcentrationForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): LegacyMeasurement | null {
  const evaluatedCell = getOptionalEvaluatedCellOfColumnType({
    columnDefinitionType: TableColumnType.CONCENTRATION,
    tableColumnDefinitions: columnDefinitions,
    evaluatedRow: evaluatedEditableRow,
  });

  if (evaluatedCell == null) {
    return null;
  }
  const {value: typedValue} = evaluatedCell;
  if (isTypedValueEmpty(typedValue)) {
    return null;
  }

  assert(isNumeric(typedValue), 'Unexpected typedValue in concentration cell', {
    typedValue,
  });

  const primitive = coerceToPrimitive(typedValue);
  const concentrationValue = typeof primitive === 'number' ? primitive : null;
  const concentrationColumnDefinition = _.findWhere(columnDefinitions, {
    type: TableColumnType.CONCENTRATION,
  }) as ConcentrationColumnDefinition;
  return {
    value: concentrationValue,
    units: concentrationColumnDefinition.units,
  };
}

export function getContainerSchemaTagsDictForRow(
  evaluatedEditableRow: Array<EvaluatedCell>,
  columnDefinitions: Array<ColumnDefinition>
): {[fieldName: string]: Partial<Tag>} {
  const containerSchemaTagsDict: {[fieldName: string]: Partial<Tag>} = {};
  for (const [i, columnDefinition] of columnDefinitions.entries()) {
    if (columnDefinition.type === TableColumnType.TAG_VALUE) {
      const {value: typedValue} = evaluatedEditableRow[i];
      // The function is used for performing the transfer operation so its ok to access live schemaField
      const tagValue = convertTypedValueToTag(typedValue, columnDefinition.schemaField);
      if (tagValue) {
        containerSchemaTagsDict[columnDefinition.schemaField.name] = getTagUpdateParams(tagValue);
      }
    }
  }
  return containerSchemaTagsDict;
}
